// Centralized API helper for Transiter

// In production (Cloudflare Pages), use our own proxy function.
// In local dev, CRA's proxy in package.json handles CORS for us.
const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

export const API_BASE = isLocal ? "/systems" : "/api/proxy";
export const TRANSITER_ORIGIN = "https://demo.transiter.dev";

// Build the correct URL depending on environment
function buildUrl(path) {
  if (isLocal) return path;
  return `${API_BASE}?url=${encodeURIComponent(`${TRANSITER_ORIGIN}${path}`)}`;
}
export const SYSTEM_ID = "us-ny-subway";

export async function fetchStops(systemId = SYSTEM_ID) {
  // Paginate through all stops - API returns too much data per stop
  // so we need to fetch in batches and filter for STATION type only
  const allStops = [];
  let firstId = "";
  const limit = 100;

  for (let i = 0; i < 20; i++) { // safety limit: 20 pages * 100 = 2000 max
    const params = firstId ? `?limit=${limit}&first_id=${firstId}` : `?limit=${limit}`;
    const url = buildUrl(`/systems/${systemId}/stops${params}`);
    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to fetch stops");
    const data = await res.json();
    const stops = data.stops || [];
    if (stops.length === 0) break;

    // Only keep STATION-type stops (not PLATFORM child stops like G12N, G12S)
    const stations = stops.filter(s => s.type === "STATION");
    allStops.push(...stations);

    // Next page: use last stop's ID
    if (stops.length < limit) break;
    firstId = stops[stops.length - 1].id;
    // Skip to next ID after the last one (Transiter first_id is inclusive)
    // We need to add a character to go past it
    firstId = firstId + "~";
  }

  return { stops: allStops };
}

export async function fetchArrivals(systemId, stopId) {
  const url = buildUrl(`/systems/${systemId}/stops/${stopId}`);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch arrivals");
  return res.json();
}

export async function fetchRoutes(systemId = SYSTEM_ID) {
  const url = buildUrl(`/systems/${systemId}/routes`);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch routes");
  return res.json();
}

// ===== MTA BusTime API helpers =====
function buildBusUrl(path) {
  // path starts with /api/where/... or /api/siri/...
  if (isLocal) {
    // CRA proxy: /busapi -> https://bustime.mta.info/api
    return path.replace(/^\/api/, "/busapi");
  }
  const full = `https://bustime.mta.info${path}`;
  return `/api/proxy?url=${encodeURIComponent(full)}`;
}

export async function fetchBusRoutes(apiKey) {
  const url = buildBusUrl(`/api/where/routes-for-agency/MTA%20NYCT.json?key=${apiKey}`);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch bus routes");
  const data = await res.json();
  // Also fetch MTABC routes
  const url2 = buildBusUrl(`/api/where/routes-for-agency/MTABC.json?key=${apiKey}`);
  const res2 = await fetch(url2);
  let mtabcRoutes = [];
  if (res2.ok) {
    const data2 = await res2.json();
    mtabcRoutes = data2?.data?.list || [];
  }
  return [...(data?.data?.list || []), ...mtabcRoutes];
}

export async function fetchBusStopsForRoute(routeId, apiKey) {
  const url = buildBusUrl(`/api/where/stops-for-route/${encodeURIComponent(routeId)}.json?key=${apiKey}&includePolylines=false&version=2`);
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch bus stops");
  const data = await res.json();
  const entry = data?.data?.entry || {};
  const stopsRef = data?.data?.references?.stops || [];
  const stopMap = {};
  stopsRef.forEach(s => { stopMap[s.id] = s; });

  // Extract directions with their stops
  const groupings = entry.stopGroupings || [];
  const directions = [];
  (groupings[0]?.stopGroups || []).forEach(g => {
    directions.push({
      id: g.id,
      name: g.name?.name || `Direction ${g.id}`,
      stops: (g.stopIds || []).map(sid => stopMap[sid]).filter(Boolean),
    });
  });
  return directions;
}